/**
 * PrintReport.js
 * ---------------------------------------------------------------
 * New, self-contained print helpers (does not use Print.js).
 *
 * 1. printAttendanceDashboard(...)  -> builds a clean report from DATA
 *                                      (no html2canvas, no DOM copy).
 * 2. printModalContent(id, opts)    -> prints any list shown inside a modal
 *                                      (Long Absent, Low Attendance, Student
 *                                      lists, summary tables ...).
 *
 * Both print through a hidden iframe, so there are no popup-blocker
 * problems and no blank window.
 */

/* ============================== helpers ============================== */

const esc = (v) =>
  String(v ?? "").replace(
    /[&<>"']/g,
    (c) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[
        c
      ],
  );

const num = (v) => Number(v || 0);

const collectPageStyles = () =>
  Array.from(document.querySelectorAll('link[rel="stylesheet"], style'))
    .map((node) => node.outerHTML)
    .join("\n");

const waitForAssets = async (doc) => {
  const pending = [];

  doc.querySelectorAll('link[rel="stylesheet"]').forEach((link) => {
    if (!link.sheet) {
      pending.push(
        new Promise((resolve) => {
          link.addEventListener("load", resolve, { once: true });
          link.addEventListener("error", resolve, { once: true });
        }),
      );
    }
  });

  doc.querySelectorAll("img").forEach((img) => {
    if (!img.complete) {
      pending.push(
        new Promise((resolve) => {
          img.addEventListener("load", resolve, { once: true });
          img.addEventListener("error", resolve, { once: true });
        }),
      );
    }
  });

  // never hang forever on a slow asset
  await Promise.race([
    Promise.all(pending),
    new Promise((resolve) => setTimeout(resolve, 5000)),
  ]);

  try {
    await doc.fonts?.ready;
  } catch (e) {
    /* ignore */
  }
};

const printHtmlInIframe = async (html) => {
  const iframe = document.createElement("iframe");
  iframe.setAttribute("aria-hidden", "true");
  iframe.style.cssText =
    "position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;";
  document.body.appendChild(iframe);

  const win = iframe.contentWindow;
  const doc = win.document;

  doc.open();
  doc.write(html);
  doc.close();

  await waitForAssets(doc);
  // small delay so layout settles before the print dialog
  await new Promise((resolve) => setTimeout(resolve, 150));

  let cleaned = false;
  const cleanup = () => {
    if (cleaned) return;
    cleaned = true;
    setTimeout(() => iframe.remove(), 300);
  };

  win.onafterprint = cleanup;
  win.focus();
  win.print();

  // fallback for browsers that don't fire afterprint on iframes
  setTimeout(cleanup, 120000);
};

const BASE_PRINT_CSS = `
  @page { size: A4 portrait; margin: 10mm; }
  * { box-sizing: border-box; }
  html, body {
    margin: 0; padding: 0; background: #fff !important; color: #000 !important;
    -webkit-print-color-adjust: exact; print-color-adjust: exact;
    font-family: Arial, Helvetica, sans-serif; font-size: 12px;
  }
  .pr-wrap { padding: 4px; }
  .pr-head {
    display: flex; justify-content: space-between; align-items: flex-end;
    border-bottom: 2px solid #222; padding-bottom: 8px; margin-bottom: 12px;
  }
  .pr-head h1 { margin: 0; font-size: 20px; }
  .pr-head .sub { color: #555; font-size: 11px; margin-top: 2px; }
  .pr-meta { text-align: right; font-size: 11px; line-height: 1.5; }
  .pr-meta b { color: #000; }
  .pr-sec { font-size: 13px; font-weight: 700; margin: 16px 0 6px; }
  .pr-grid { display: grid; gap: 8px; }
  .pr-g4 { grid-template-columns: repeat(4, 1fr); }
  .pr-g3 { grid-template-columns: repeat(3, 1fr); }
  .pr-g2 { grid-template-columns: repeat(2, 1fr); }
  .pr-box { border: 1px solid #bbb; border-radius: 6px; padding: 8px 10px; page-break-inside: avoid; }
  .pr-box .lbl { font-size: 10.5px; color: #555; }
  .pr-box .val { font-size: 20px; font-weight: 700; line-height: 1.2; }
  .pr-box .note { font-size: 10px; color: #555; }
  .pr-box.ok .val { color: #198754; }
  .pr-box.bad .val { color: #dc3545; }
  .pr-box.warn .val { color: #b58100; }
  .pr-box.info .val { color: #0a7a95; }
  .pr-box.pri .val { color: #0d6efd; }
  table.pr-table { width: 100%; border-collapse: collapse; font-size: 11px; }
  table.pr-table th, table.pr-table td { border: 1px solid #777; padding: 4px 6px; text-align: left; vertical-align: middle; }
  table.pr-table th { background: #eee !important; font-weight: 700; }
  table.pr-table .c { text-align: center; }
  table.pr-table thead { display: table-header-group; }
  table.pr-table tr { page-break-inside: avoid; }
  .pr-bar { background: #e5e5e5 !important; height: 6px; border-radius: 3px; overflow: hidden; margin-top: 3px; }
  .pr-bar > i { display: block; height: 100%; }
  .pr-empty { text-align: center; color: #666; padding: 14px; border: 1px dashed #aaa; }
  .pr-foot { margin-top: 16px; padding-top: 6px; border-top: 1px solid #aaa; font-size: 10px; color: #666; display: flex; justify-content: space-between; }
`;

const pctColor = (p) => (p >= 75 ? "#198754" : p >= 50 ? "#ffc107" : "#dc3545");

const nowText = () => new Date().toLocaleString("en-IN");

/* ================= 1. FULL DASHBOARD REPORT (from data) ================= */

/**
 * @param {object} p
 * @param {object} p.data           daily summary (total/present/absent/not_marked/push_message)
 * @param {object} p.analyticsData  batch_wise, staff_wise, long_absent, low_attendance_below_75
 * @param {object} p.dataSummary    range summary (regular_present, absent_present)
 * @param {object} p.percentage     { present, absent, notMarked }
 * @param {string} p.sessionName
 * @param {string} p.date
 * @param {string} p.fromDate
 * @param {string} p.toDate
 * @param {string} [p.title]
 */
export const printAttendanceDashboard = async ({
  data = {},
  analyticsData = {},
  dataSummary = {},
  percentage = { present: 0, absent: 0, notMarked: 0 },
  sessionName = "-",
  date = "",
  fromDate = "",
  toDate = "",
  title = "Student Attendance Report",
}) => {
  const batches = analyticsData?.batch_wise || [];
  const staff = analyticsData?.staff_wise || [];

  const batchRows = batches.length
    ? batches
        .map((b, i) => {
          const total = num(b?.total_student);
          const present = num(b?.present);
          const p = total > 0 ? Math.floor((present / total) * 100) : 0;
          return `
          <tr>
            <td class="c">${i + 1}</td>
            <td>${esc(b?.batch_name || "-")}${
              b?.class_teacher_name
                ? `<br/><small style="color:#555">Teacher: ${esc(b.class_teacher_name)}</small>`
                : ""
            }</td>
            <td class="c">${total}</td>
            <td class="c">${present}</td>
            <td class="c">${num(b?.absent)}</td>
            <td class="c">${num(b?.no_marked)}</td>
            <td style="min-width:90px">
              <b>${p}%</b>
              <div class="pr-bar"><i style="width:${p}%;background:${pctColor(p)}"></i></div>
            </td>
          </tr>`;
        })
        .join("")
    : `<tr><td colspan="7" class="pr-empty">No batch attendance data found.</td></tr>`;

  const staffRows = staff.length
    ? staff
        .map((s, i) => {
          const total = num(s?.total_student);
          const present = num(s?.present);
          const p = total > 0 ? Math.round((present / total) * 100) : 0;
          return `
          <tr>
            <td class="c">${i + 1}</td>
            <td>${esc(s?.staff_name || "-")}<br/><small style="color:#555">ID: ${esc(s?.staff_id || "-")}</small></td>
            <td class="c">${total}</td>
            <td class="c">${present}</td>
            <td class="c">${num(s?.absent)}</td>
            <td class="c">${num(s?.not_marked)}</td>
            <td style="min-width:90px">
              <b>${p}%</b>
              <div class="pr-bar"><i style="width:${p}%;background:${pctColor(p)}"></i></div>
            </td>
          </tr>`;
        })
        .join("")
    : "";

  const msgBox = (label, cls, val) =>
    `<div class="pr-box ${cls}"><div class="lbl">${label}</div><div class="val">${num(val)}</div></div>`;

  const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${esc(title)}</title>
  <style>${BASE_PRINT_CSS}</style>
</head>
<body>
  <div class="pr-wrap">

    <div class="pr-head">
      <div>
        <h1>${esc(title)}</h1>
        <div class="sub">Daily student attendance overview</div>
      </div>
      <div class="pr-meta">
        <div>Session: <b>${esc(sessionName)}</b></div>
        <div>Date: <b>${esc(date)}</b></div>
      </div>
    </div>

    <div class="pr-grid pr-g4">
      <div class="pr-box pri"><div class="lbl">Total Students</div><div class="val">${num(data?.total)}</div><div class="note">Active students</div></div>
      <div class="pr-box ok"><div class="lbl">Present</div><div class="val">${num(data?.present)}</div><div class="note">${percentage.present}% attendance</div></div>
      <div class="pr-box bad"><div class="lbl">Absent</div><div class="val">${num(data?.absent)}</div><div class="note">${percentage.absent}% absent</div></div>
      <div class="pr-box warn"><div class="lbl">Not Marked</div><div class="val">${num(data?.not_marked)}</div><div class="note">${percentage.notMarked}% pending</div></div>
    </div>

    <div class="pr-sec">Highlights</div>
    <div class="pr-grid pr-g3">
      <div class="pr-box bad"><div class="lbl">Low Attendance (below 75%)</div><div class="val">${num(analyticsData?.low_attendance_below_75)}</div></div>
      <div class="pr-box warn"><div class="lbl">Long Absent</div><div class="val">${num(analyticsData?.long_absent)}</div></div>
      <div class="pr-box info"><div class="lbl">Class Teachers</div><div class="val">${staff.length}</div></div>
    </div>

    <div class="pr-sec">Message Delivery</div>
    <div class="pr-grid pr-g2">
      <div>
        <div style="font-weight:700;margin-bottom:4px">Push Notification</div>
        <div class="pr-grid pr-g2">
          ${msgBox("Not Sent", "warn", data?.push_not_sent)}
          ${msgBox("Sent", "ok", data?.push_sent)}
          ${msgBox("Processing", "info", data?.push_processing)}
          ${msgBox("Failed", "bad", data?.push_failed)}
        </div>
      </div>
      <div>
        <div style="font-weight:700;margin-bottom:4px">Meta WhatsApp</div>
        <div class="pr-grid pr-g2">
          ${msgBox("Not Sent", "warn", data?.message_not_sent)}
          ${msgBox("Sent", "ok", data?.message_sent)}
          ${msgBox("Processing", "info", data?.message_processing)}
          ${msgBox("Failed", "bad", data?.message_failed)}
        </div>
      </div>
    </div>

    <div class="pr-sec">Attendance Summary (${esc(fromDate)} to ${esc(toDate)})</div>
    <div class="pr-grid pr-g3">
      <div class="pr-box ok"><div class="lbl">Regular Present</div><div class="val">${num(dataSummary?.regular_present)}</div></div>
      <div class="pr-box bad"><div class="lbl">Regular Absent</div><div class="val">${num(dataSummary?.absent_present)}</div></div>
      <div class="pr-box warn"><div class="lbl">Absent / Present</div><div class="val">${num(analyticsData?.attendance_summary?.absent_present)}</div></div>
    </div>

    <div class="pr-sec">Batch Wise Attendance</div>
    <table class="pr-table">
      <thead>
        <tr>
          <th class="c" style="width:30px">#</th>
          <th>Batch</th>
          <th class="c">Total</th>
          <th class="c">Present</th>
          <th class="c">Absent</th>
          <th class="c">Not Marked</th>
          <th>Attendance</th>
        </tr>
      </thead>
      <tbody>${batchRows}</tbody>
    </table>

    ${
      staff.length
        ? `<div class="pr-sec">Staff Wise Attendance</div>
    <table class="pr-table">
      <thead>
        <tr>
          <th class="c" style="width:30px">#</th>
          <th>Staff</th>
          <th class="c">Total Students</th>
          <th class="c">Present</th>
          <th class="c">Absent</th>
          <th class="c">Not Marked</th>
          <th>Attendance</th>
        </tr>
      </thead>
      <tbody>${staffRows}</tbody>
    </table>`
        : ""
    }

    <div class="pr-foot">
      <span>Printed on ${esc(nowText())}</span>
      <span>${esc(title)}</span>
    </div>
  </div>
</body>
</html>`;

  await printHtmlInIframe(html);
};

/* ============== 2. PRINT ANY LIST SHOWN INSIDE A MODAL ============== */

/**
 * Prints the content of an element (e.g. the Long Absent / Low Attendance
 * student list) in a clean page.
 *
 * @param {string} elementId   id of the wrapper around the list
 * @param {object} [opts]
 * @param {string} [opts.title]
 * @param {string} [opts.subtitle]
 * @param {string[]} [opts.meta]        e.g. ["Session: 2026-27", "Date: 2026-10-13"]
 * @param {boolean} [opts.landscape]    true for wide tables
 */
export const printModalContent = async (
  elementId,
  {
    title = "Report",
    subtitle = "",
    meta = [],
    landscape = false,
    tablesOnly = true, // print only the data tables (no search / "Showing x of y" bars)
  } = {},
) => {
  const source = document.getElementById(elementId);

  if (!source) {
    alert("Nothing to print.");
    return;
  }

  const clone = source.cloneNode(true);

  // 1) turn live form controls into plain text (cloneNode loses typed values)
  const srcCtrls = source.querySelectorAll("input, select, textarea");
  const cloneCtrls = clone.querySelectorAll("input, select, textarea");

  cloneCtrls.forEach((el, i) => {
    const s = srcCtrls[i];
    if (!s || s.type === "hidden") {
      el.remove();
      return;
    }

    let text = "";
    if (s.tagName === "SELECT") {
      text = s.options[s.selectedIndex]?.text ?? "";
    } else if (s.type === "checkbox" || s.type === "radio") {
      text = s.checked ? "\u2714" : "";
    } else {
      text = s.value ?? "";
    }

    el.replaceWith(document.createTextNode(text));
  });

  // 2) remove everything that should not be on paper
  clone
    .querySelectorAll(
      [
        ".no-print",
        "button",
        ".btn",
        ".pagination",
        "nav",
        "script",
        "[data-bs-dismiss]",
        ".dataTables_filter",
        ".dataTables_length",
        ".dataTables_paginate",
        // icons, avatars and sort arrows
        'i[class*="ri-"]',
        'i[class*="bx"]',
        'i[class*="fa-"]',
        'i[class*="mdi"]',
        'i[class*="icon"]',
        "svg",
        ".avatar",
        ".avatar-initial",
      ].join(","),
    )
    .forEach((n) => n.remove());

  // 3) drop duplicate screen headings / dividers (the print header replaces them)
  clone.querySelector("h4")?.remove();
  clone.querySelectorAll("hr").forEach((n) => n.remove());

  // 4) drop the Action column (and any select/checkbox column that is now blank)
  const ACTION_HEADS = ["action", "actions", "select", "options", "option"];

  clone.querySelectorAll("table").forEach((table) => {
    const headRow = table.tHead?.rows?.[0] || table.rows[0];
    if (!headRow) return;

    const colCount = headRow.cells.length;
    const bodyRows = Array.from(table.rows).filter(
      (r) => r !== headRow && r.cells.length === colCount,
    );

    const drop = [];

    for (let c = 0; c < colCount; c++) {
      const head = headRow.cells[c].textContent.trim().toLowerCase();
      const isActionHead = ACTION_HEADS.includes(head);

      const blankBody =
        bodyRows.length > 0 &&
        bodyRows.every((r) => {
          const cell = r.cells[c];
          const txt = cell.textContent.trim();
          return (txt === "" || txt === "\u2714") && !cell.querySelector("img");
        });

      if (isActionHead || (head === "" && blankBody)) drop.push(c);
    }

    if (!drop.length) return;

    Array.from(table.rows).forEach((row) => {
      if (row.cells.length === colCount) {
        [...drop].reverse().forEach((c) => row.deleteCell(c));
      } else if (row.cells.length === 1) {
        // "No data found" style row spanning all columns
        row.cells[0].colSpan = Math.max(1, colCount - drop.length);
      }
    });
  });

  // 5) keep ONLY the data table(s): drops the search bar, "Showing 1-100 of N",
  //    filters and any other toolbar that sits above/below the table
  if (tablesOnly) {
    const tables = Array.from(clone.querySelectorAll("table")).filter(
      (t) => !t.parentElement?.closest("table"),
    );

    if (tables.length) {
      const wrapper = document.createElement("div");
      tables.forEach((t) => {
        t.removeAttribute("style");
        wrapper.appendChild(t);
      });
      clone.replaceChildren(wrapper);
    }
  }

  // record count for the print header (rows actually in the table)
  let recordCountText = "";
  const firstTable = clone.querySelector("table");
  if (firstTable) {
    const headRow = firstTable.tHead?.rows?.[0] || firstTable.rows[0];
    const rowsList = Array.from(
      firstTable.tBodies.length ? firstTable.tBodies[0].rows : firstTable.rows,
    ).filter((r) => r !== headRow && r.cells.length > 1);
    if (rowsList.length) recordCountText = `Total records: ${rowsList.length}`;
  }

  if (!clone.textContent.trim()) {
    alert("Nothing to print yet. Please wait for the list to load.");
    return;
  }

  const metaHtml = [...(meta || []), recordCountText]
    .filter(Boolean)
    .map((m) => `<div>${esc(m)}</div>`)
    .join("");

  const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <base href="${esc(document.baseURI)}" />
  <title>${esc(title)}</title>
  ${collectPageStyles()}
  <style>
    ${BASE_PRINT_CSS}
    @page { size: A4 ${landscape ? "landscape" : "portrait"}; margin: 10mm; }

    /* neutralise app layout so the list always shows */
    body { display: block !important; height: auto !important; overflow: visible !important; }
    .pr-content, .pr-content * { visibility: visible !important; }
    .pr-content .table-responsive { overflow: visible !important; }
    .pr-content .card { box-shadow: none !important; border: none !important; }
    .pr-content table { width: 100% !important; font-size: 11px; border-collapse: collapse; }
    .pr-content th, .pr-content td { border: 1px solid #777 !important; padding: 4px 6px !important; color: #000 !important; }
    .pr-content th { background: #eee !important; }
    .pr-content thead { display: table-header-group; }
    .pr-content table { page-break-inside: auto !important; margin: 0 !important; }
    .pr-content tr { page-break-inside: avoid; }
    .pr-content .badge { border: 1px solid #999; color: #000 !important; background: #fff !important; }
    .pr-content a { color: inherit !important; text-decoration: none !important; }
    .pr-content .text-nowrap { white-space: normal !important; }
    .pr-content .d-none { display: block !important; }
    .pr-content .d-print-none { display: none !important; }
  </style>
</head>
<body>
  <div class="pr-wrap">
    <div class="pr-head">
      <div>
        <h1>${esc(title)}</h1>
        ${subtitle ? `<div class="sub">${esc(subtitle)}</div>` : ""}
      </div>
      <div class="pr-meta">${metaHtml}</div>
    </div>

    <div class="pr-content">${clone.innerHTML}</div>

    <div class="pr-foot">
      <span>Printed on ${esc(nowText())}</span>
      <span>${esc(title)}</span>
    </div>
  </div>
</body>
</html>`;

  await printHtmlInIframe(html);
};
